import { t } from '@lingui/core/macro'
import type { RecordedAgenticChatSession } from 'features/embeddedSidebar/hooks/useAgenticChatSessionHistory'
import type { RolodexEntryQueryBody } from 'features/rolodex'
import {
  type RolodexEntryPatchJson,
  RolodexEntryType,
  RolodexFilterOp,
} from 'types'
import type { z } from 'zod'

import {
  DOCUMENT_CHAT_CONVERSATION_FILE_PATH,
  DOCUMENT_CHAT_SESSION_PAGE_SIZE,
  DOCUMENT_CHAT_STORED_TURN_ID_PREFIX,
  DOCUMENT_CHAT_UI_HISTORY_SCHEMA_VERSION,
  DOCUMENT_CHAT_USER_TAG,
  type DocumentChatActiveTurn,
  documentChatActiveTurnSchema,
  type DocumentChatStoredTurn,
  documentChatUiHistorySchema,
  storedTurnsFromAgentTurns,
} from './documentChatSessionSchemas'

export type AgenticChatSessionSyncError =
  | 'rename'
  | 'archive'
  | 'unarchive'
  | 'delete'
  | 'hydrate'
  | 'pin'
  | 'unpin'

type DocumentChatSessionMetadata = {
  activeTurn?: DocumentChatActiveTurn
  archived: boolean
  archivedAt?: number
  id: string
  lastCompletedAt: number
  lastCompletedTurnId?: string
  listUpdatedAt: number
  pendingUserQuery?: string
  pinned?: boolean
  pinnedAt?: number
  snapshotPath?: string
  storedTurns?: DocumentChatStoredTurn[]
  title: string
  visibleTurnCount: number
}

type ParsedUiHistory = z.infer<typeof documentChatUiHistorySchema>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

function rolodexUpdatedAt(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function rolodexEntry(value: unknown): {
  data: Record<string, unknown>
  id: string
  type: string
  updatedAt: number
} | null {
  if (!isRecord(value)) return null
  const entry = isRecord(value.entry) ? value.entry : value
  if (
    typeof entry.id !== 'string' ||
    typeof entry.type !== 'string' ||
    !isRecord(entry.data)
  ) {
    return null
  }
  return {
    data: entry.data,
    id: entry.id,
    type: entry.type,
    updatedAt: rolodexUpdatedAt(entry.updated_at),
  }
}

function parseUiHistory(
  historyRaw: unknown
): ParsedUiHistory | undefined | null {
  if (historyRaw === undefined) return undefined
  const parsed = documentChatUiHistorySchema.safeParse(historyRaw)
  return parsed.success ? parsed.data : null
}

function historyListFlags(
  history: ParsedUiHistory | undefined
): Pick<DocumentChatSessionMetadata, 'archivedAt' | 'pinned' | 'pinnedAt'> {
  return {
    ...(typeof history?.archived_at === 'number'
      ? { archivedAt: history.archived_at }
      : {}),
    ...(history?.pinned === true ? { pinned: true } : {}),
    ...(typeof history?.pinned_at === 'number'
      ? { pinnedAt: history.pinned_at }
      : {}),
  }
}

function metadataFromSnapshotHistory(
  entry: { id: string; updatedAt: number },
  history: ParsedUiHistory
): DocumentChatSessionMetadata | null {
  if (
    history.snapshot_path !== DOCUMENT_CHAT_CONVERSATION_FILE_PATH ||
    typeof history.last_completed_at !== 'number'
  ) {
    return null
  }
  return {
    archived: history.archived === true,
    id: entry.id,
    lastCompletedAt: history.last_completed_at,
    listUpdatedAt: entry.updatedAt,
    snapshotPath: history.snapshot_path,
    title: history.title?.trim() || t`Agentic search`,
    visibleTurnCount: history.visible_turn_count ?? 0,
    ...historyListFlags(history),
    ...(history.last_completed_turn_id
      ? { lastCompletedTurnId: history.last_completed_turn_id }
      : {}),
  }
}

function metadataFromStoredAgentTurns(
  entry: { data: Record<string, unknown>; id: string; updatedAt: number },
  history: ParsedUiHistory | undefined
): DocumentChatSessionMetadata | null {
  const storedTurns = storedTurnsFromAgentTurns(entry.data)
  const pendingUserQuery = storedTurns.at(-1)?.query
  if (!pendingUserQuery) return null
  const lastCompletedIndex = storedTurns.findLastIndex(
    (turn) => turn.finalResponse !== undefined
  )
  return {
    archived: history?.archived === true,
    id: entry.id,
    lastCompletedAt: entry.updatedAt,
    listUpdatedAt: entry.updatedAt,
    pendingUserQuery,
    storedTurns,
    title: history?.title?.trim() || pendingUserQuery,
    visibleTurnCount: storedTurns.length,
    ...(lastCompletedIndex >= 0
      ? {
          lastCompletedTurnId: `${DOCUMENT_CHAT_STORED_TURN_ID_PREFIX}${lastCompletedIndex}`,
        }
      : {}),
    ...historyListFlags(history),
  }
}

function parseActiveTurn(
  data: Record<string, unknown>
): DocumentChatActiveTurn | undefined {
  const parsed = documentChatActiveTurnSchema.safeParse(data.active_turn)
  if (!parsed.success) return undefined
  const { error, ...rest } = parsed.data
  const trimmed = error?.trim()
  return trimmed ? { ...rest, error: trimmed } : rest
}

function withActiveTurn(
  metadata: DocumentChatSessionMetadata | null,
  activeTurn: DocumentChatActiveTurn | undefined
): DocumentChatSessionMetadata | null {
  if (!metadata) return null
  return activeTurn === undefined ? metadata : { ...metadata, activeTurn }
}

export function parseDocumentChatSessionMetadata(
  value: unknown
): DocumentChatSessionMetadata | null {
  const entry = rolodexEntry(value)
  if (!entry || entry.type !== RolodexEntryType.POWER_DOC_SEARCH_CONVERSATION) {
    return null
  }
  const history = parseUiHistory(entry.data.ui_history)
  if (history === null) return null
  if (history?.deleted === true) return null
  const activeTurn = parseActiveTurn(entry.data)
  const snapshotMeta =
    history === undefined ? null : metadataFromSnapshotHistory(entry, history)
  const storedMeta = metadataFromStoredAgentTurns(entry, history)
  if (
    activeTurn?.status === 'running' ||
    activeTurn?.status === 'failed' ||
    activeTurn?.status === 'cancelled'
  ) {
    // Keep snapshot identity and entry turns for a failed snapshot download.
    if (snapshotMeta && storedMeta) {
      return {
        ...snapshotMeta,
        activeTurn,
        pendingUserQuery: storedMeta.pendingUserQuery,
        storedTurns: storedMeta.storedTurns,
        visibleTurnCount: storedMeta.visibleTurnCount,
      }
    }
    return withActiveTurn(storedMeta ?? snapshotMeta, activeTurn)
  }
  return withActiveTurn(snapshotMeta ?? storedMeta, activeTurn)
}

function conversationUiHistoryPatch(
  conversationId: string,
  uiHistory: Record<string, unknown>,
  extras: Partial<RolodexEntryPatchJson> = {}
): RolodexEntryPatchJson {
  return {
    id: conversationId,
    type: RolodexEntryType.POWER_DOC_SEARCH_CONVERSATION,
    data: {
      ui_history: {
        schema_version: DOCUMENT_CHAT_UI_HISTORY_SCHEMA_VERSION,
        ...uiHistory,
      },
    },
    ...extras,
  }
}

export function buildDocumentChatSessionRenamePatch(
  conversationId: string,
  title: string
): RolodexEntryPatchJson {
  return conversationUiHistoryPatch(conversationId, { title })
}

export function buildDocumentChatSessionPinPatch({
  conversationId,
  pinned,
  pinnedAt,
}: {
  conversationId: string
  pinned: boolean
  pinnedAt?: number
}): RolodexEntryPatchJson {
  return conversationUiHistoryPatch(
    conversationId,
    {
      pinned,
      ...(pinned && pinnedAt !== undefined ? { pinned_at: pinnedAt } : {}),
    },
    pinned ? {} : { delete_data: ['ui_history.pinned_at'] }
  )
}

export function buildDocumentChatSessionArchivePatch({
  archived,
  archivedAt,
  conversationId,
}: {
  archived: boolean
  archivedAt?: number
  conversationId: string
}): RolodexEntryPatchJson {
  return conversationUiHistoryPatch(
    conversationId,
    {
      archived,
      ...(archived && archivedAt !== undefined
        ? { archived_at: archivedAt }
        : {}),
    },
    archived ? {} : { delete_data: ['ui_history.archived_at'] }
  )
}

export function buildDocumentChatSessionDeletePatch({
  conversationId,
  deletedAt,
}: {
  conversationId: string
  deletedAt: number
}): RolodexEntryPatchJson {
  return conversationUiHistoryPatch(conversationId, {
    deleted: true,
    deleted_at: deletedAt,
  })
}

export function buildDocumentChatSessionLoadOptions({
  archived,
  limit = DOCUMENT_CHAT_SESSION_PAGE_SIZE,
  offset = 0,
  pinned,
  siteId,
  userId,
}: {
  archived: boolean
  limit?: number
  offset?: number
  pinned?: boolean
  siteId: string | undefined
  userId: string | undefined
}): RolodexEntryQueryBody | undefined {
  if (!siteId || !userId) return undefined
  return {
    type: RolodexEntryType.POWER_DOC_SEARCH_CONVERSATION,
    tags: {
      [RolodexEntryType.SITE]: siteId,
      [DOCUMENT_CHAT_USER_TAG]: userId,
    } as RolodexEntryQueryBody['tags'],
    filters: [
      {
        field: 'data',
        key: 'ui_history.archived',
        op: archived ? RolodexFilterOp.TRUTHY : RolodexFilterOp.FALSY,
      },
      {
        field: 'data',
        key: 'ui_history.deleted',
        op: RolodexFilterOp.FALSY,
      },
      ...(pinned === true
        ? [
            {
              field: 'data' as const,
              key: 'ui_history.pinned',
              op: RolodexFilterOp.TRUTHY,
            },
          ]
        : []),
    ],
    order_by: [{ field: 'updated_at', descending: 'true' }],
    limit,
    offset,
  }
}

export function sessionsFromRolodexEntries(
  values: readonly unknown[] | undefined,
  siteId: string
): RecordedAgenticChatSession[] {
  return (values ?? []).flatMap((value) => {
    const metadata = parseDocumentChatSessionMetadata(value)
    if (!metadata) return []
    return [
      {
        archived: metadata.archived,
        conversation: { id: metadata.id, turns: [] },
        isHydrated: false,
        key: metadata.id,
        siteId,
        sourceTabId: metadata.id,
        title: metadata.title,
        updatedAt: metadata.listUpdatedAt,
        visibleTurnCount: metadata.visibleTurnCount,
        ...(metadata.lastCompletedTurnId === undefined
          ? {}
          : { lastCompletedTurnId: metadata.lastCompletedTurnId }),
        ...(metadata.pendingUserQuery === undefined
          ? {}
          : { pendingUserQuery: metadata.pendingUserQuery }),
        ...(metadata.storedTurns === undefined
          ? {}
          : { storedTurns: metadata.storedTurns }),
        ...(metadata.activeTurn === undefined
          ? {}
          : { activeTurn: metadata.activeTurn }),
        ...(metadata.snapshotPath === undefined
          ? {}
          : { snapshotPath: metadata.snapshotPath }),
        ...(metadata.pinned === true ? { pinned: true } : {}),
        ...(metadata.pinnedAt === undefined
          ? {}
          : { pinnedAt: metadata.pinnedAt }),
      },
    ]
  })
}
